import {
  INDEXABLE_TYPES,
  TRANSLATABLE_TYPES,
  ENUM_TYPES,
  SOURCE_TYPES_ALLOWED,
  NESTED_TYPES,
  WIDGET_TYPES,
} from './schemaConstants'

const KEY_REGEX = /^[a-z][a-z0-9_]*$/

let uidCounter = 0

/**
 * Generate a unique field id, used as a Vue :key inside the schema constructor.
 */
export function makeUid() {
  return 'f_' + (++uidCounter) + '_' + Math.random().toString(36).substr(2, 5)
}

/**
 * Recursively (re)assign a fresh _uid to a field and all of its nested fields.
 * Mutates the passed field and returns it.
 */
export function reassignUids(field) {
  field._uid = makeUid()
  if (Array.isArray(field.schema)) {
    field.schema.forEach(reassignUids)
  }
  return field
}

/**
 * Validate schema before saving
 * @param {Array} fields
 * @param {Array} fieldsets
 * @returns {string[]} array of error messages
 */
export function validateSchema(fields, fieldsets) {
  const errors = []
  const warnings = []
  const fieldsetKeys = new Set(fieldsets.map(fs => fs.key))

  // Validate fieldsets
  const seenFsKeys = new Set()
  for (const fs of fieldsets) {
    if (!fs.key) {
      errors.push('Fieldset is missing a key')
      continue
    }
    if (!KEY_REGEX.test(fs.key)) {
      errors.push(`Fieldset key "${fs.key}" must start with a letter and contain only lowercase letters, numbers, underscores`)
    }
    if (seenFsKeys.has(fs.key)) {
      errors.push(`Duplicate fieldset key "${fs.key}"`)
    }
    seenFsKeys.add(fs.key)

    if (fs.depends_on) {
      validateDependsOn(fs.depends_on, fields, errors, `Fieldset "${fs.key}"`, fields)
    }
  }

  // Validate fields recursively
  validateFieldsLevel(fields, fieldsetKeys, errors, warnings, 'root', fields)

  return { errors, warnings }
}

function validateFieldsLevel(fields, fieldsetKeys, errors, warnings, path, rootFields) {
  const seenKeys = new Set()

  for (let i = 0; i < fields.length; i++) {
    const field = fields[i]
    const fsLabel = path === 'root' && field.fieldset ? `[${field.fieldset}] ` : ''
    const prefix = path === 'root' ? `${fsLabel}Field #${i + 1}` : `${path} → field #${i + 1}`

    // fragment doesn't need key/label
    if (field.type === 'fragment') {
      if (!field.$ref) {
        errors.push(`${prefix}: fragment must have a $ref`)
      }
      continue
    }

    if (!field.key) {
      errors.push(`${prefix}: key is required`)
      continue
    }
    if (!KEY_REGEX.test(field.key)) {
      errors.push(`${prefix} "${field.key}": invalid key format`)
    }
    if (seenKeys.has(field.key)) {
      errors.push(`${prefix}: duplicate key "${field.key}"`)
    }
    seenKeys.add(field.key)

    if (!field.label) {
      errors.push(`${prefix} "${field.key}": label is required`)
    }
    if (!field.type) {
      errors.push(`${prefix} "${field.key}": type is required`)
    }

    if (field.fieldset && fieldsetKeys && !fieldsetKeys.has(field.fieldset)) {
      errors.push(`${prefix} "${field.key}": references non-existent fieldset "${field.fieldset}"`)
    }

    if (field.depends_on) {
      validateDependsOn(field.depends_on, fields, errors, `Field "${field.key}"`, rootFields)
    }

    if (field.type === 'object' || field.type === 'array') {
      const ref = typeof field.$ref === 'string' ? field.$ref.trim() : null
      const hasSchema = Array.isArray(field.schema)
      if (field.$ref !== undefined && !ref) {
        warnings.push(`${prefix} "${field.key}": no fragment selected — the empty $ref will be dropped on save`)
      } else if (!ref && !hasSchema) {
        errors.push(`${prefix} "${field.key}": ${field.type} has no fields and no fragment reference`)
      }
      if (hasSchema) {
        validateFieldsLevel(field.schema, null, errors, warnings, `${path}.${field.key}`, rootFields)
      }
    }
  }
}

function resolveFieldPath(path, rootFields) {
  const parts = path.split('.')
  let fields = rootFields
  for (let i = 0; i < parts.length; i++) {
    const field = fields.find(f => f.key === parts[i])
    if (!field) {
      return false
    }
    if (i < parts.length - 1) {
      if (!field.schema || !Array.isArray(field.schema)) {
        return false
      }
      fields = field.schema
    }
  }
  return true
}

function validateDependsOn(dependsOn, fields, errors, context, rootFields) {
  const allFields = rootFields || fields
  if (Array.isArray(dependsOn)) {
    for (const group of dependsOn) {
      if (Array.isArray(group)) {
        for (const cond of group) {
          if (cond.field && !resolveFieldPath(cond.field, allFields)) {
            errors.push(`${context}: depends_on references non-existent field "${cond.field}"`)
          }
        }
      }
    }
  } else {
    for (const depKey of Object.keys(dependsOn)) {
      if (!resolveFieldPath(depKey, allFields)) {
        errors.push(`${context}: depends_on references non-existent field "${depKey}"`)
      }
    }
  }
}

/**
 * Clean field object: remove properties not applicable to the field type
 */
export function cleanFieldForType(field) {
  const t = field.type

  if (t === 'fragment') {
    const cleaned = { type: 'fragment' }
    const ref = typeof field.$ref === 'string' ? field.$ref.trim() : field.$ref
    if (ref) {
      cleaned.$ref = ref
    }
    return cleaned
  }

  const cleaned = { key: field.key, label: field.label, type: field.type }

  if (field.fieldset) {
    cleaned.fieldset = field.fieldset
  }

  if (field.cols) {
    cleaned.cols = field.cols
  }

  if (INDEXABLE_TYPES.includes(t) && field.indexed) {
    cleaned.indexed = true
  }
  if (TRANSLATABLE_TYPES.includes(t) && field.translatable) {
    cleaned.translatable = true
  }
  if (ENUM_TYPES.includes(t) && field.enum && field.enum.length > 0) {
    cleaned.enum = field.enum
  }
  if (WIDGET_TYPES.includes(t) && field.widget) {
    cleaned.widget = field.widget
  }
  if (t === 'object' && field.expanded !== undefined) {
    cleaned.expanded = field.expanded
  }
  if (SOURCE_TYPES_ALLOWED.includes(t) && field.source && field.source.type) {
    cleaned.source = field.source
  }
  if (NESTED_TYPES.includes(t)) {
    const ref = typeof field.$ref === 'string' ? field.$ref.trim() : field.$ref
    if (ref) {
      cleaned.$ref = ref
    } else {
      cleaned.schema = Array.isArray(field.schema) ? field.schema : []
    }
  }
  if (field.depends_on && Array.isArray(field.depends_on) && field.depends_on.length > 0) {
    cleaned.depends_on = field.depends_on
  }

  return cleaned
}

/**
 * Build output JSON from constructor state
 */
export function buildSchemaJson(fields, fieldsets) {
  const cleanedFields = fields.map(f => cleanFieldForType(f))

  const result = {}

  if (fieldsets && fieldsets.length > 0) {
    result.fieldsets = fieldsets.map(fs => {
      const out = { key: fs.key, label: fs.label }
      if (fs.expanded !== undefined) {
        out.expanded = fs.expanded
      }
      if (fs.depends_on && Object.keys(fs.depends_on).length > 0) {
        out.depends_on = fs.depends_on
      }
      return out
    })
  }

  result.fields = cleanedFields

  return result
}
